// Presentation rules for broker payout batches (SM-710). Pure
// functions — the page and the detail panel both read them, and the
// unit tests pin them — so "what may this reviewer do to this batch
// right now" is decided in exactly one place.
//
// Every rule here is broker-service's first (SM-708 §7.3–§7.4): it
// refuses a reject by the admin who prepared the batch, or a second
// decision by the same admin, with 409 BROKER_APPROVAL_INVALID (the
// requester may approve their own batch since SM-914), and any step the
// batch's state does not allow with 409
// BROKER_PAYOUT_BATCH_TRANSITION_INVALID. The console
// mirrors them so a button that would only be refused is never offered
// as live — and shows the server's code verbatim when it refuses anyway.

import { APIError } from "@/lib/api";
import type {
  PayoutBatchEarning,
  PayoutBatchItem,
  PayoutBatchState,
  PayoutSchedule,
} from "@/lib/broker-types";
import type { PageInfo } from "@/lib/types";

/**
 * The requester of every OLD scheduled batch (contracts PayoutSchedulerActor).
 * Batches are prepared by hand now (SM-878); the label stays for the
 * WEEKLY / MONTHLY batches the scheduler prepared before.
 */
export const PAYOUT_SCHEDULER_ACTOR = "system:scheduler";

const SCHEDULER = { label: "Scheduler" };

/** Who asked or acted: the scheduler by name, an admin through `name`. */
export function actorLabel(id: string, name: (adminID: string) => string): string {
  return id === PAYOUT_SCHEDULER_ACTOR ? SCHEDULER.label : name(id);
}

type Tone = "danger" | "warning" | "success" | "muted" | "accent";

// `label` keys so the page-copy extractor carries every word to the
// Thai catalogue (it reads UI property names, not bare map values).
const STATES: Record<PayoutBatchState, { label: string; tone: Tone }> = {
  DRAFT: { label: "Draft", tone: "muted" },
  PENDING_REVIEW: { label: "Awaiting review", tone: "warning" },
  APPROVED: { label: "Approved", tone: "accent" },
  PROCESSING: { label: "Processing", tone: "accent" },
  PAID: { label: "Paid", tone: "success" },
  FAILED: { label: "Failed", tone: "danger" },
  REJECTED: { label: "Rejected", tone: "muted" },
  CANCELLED: { label: "Cancelled", tone: "muted" },
};

export function batchStateLabel(state: string): string {
  return STATES[state as PayoutBatchState]?.label ?? state;
}

export function batchStateTone(state: string): Tone {
  return STATES[state as PayoutBatchState]?.tone ?? "muted";
}

const SCHEDULES: Record<PayoutSchedule, { label: string }> = {
  WEEKLY: { label: "Weekly" },
  MONTHLY: { label: "Monthly" },
  MANUAL: { label: "Manual batch" },
  NONE: { label: "Manual only" },
};

export function scheduleLabel(schedule: string): string {
  return SCHEDULES[schedule as PayoutSchedule]?.label ?? schedule;
}

/** Still moving through review or payment — money is in escrow. */
export function isOpenBatch(state: string): boolean {
  return state === "DRAFT" || state === "PENDING_REVIEW" || state === "APPROVED" || state === "PROCESSING";
}

/**
 * What a re-read of a batch can see change: its state, its approvals, the
 * time of each step and which money steps the ledger has posted. A batch
 * carries no updated_at, so these stand for it (SM-942); a restore that
 * posts after the batch was cancelled changes the broker's figures too.
 */
export function batchRevision(b: PayoutBatchItem): string {
  return [
    b.state,
    b.approvals?.length ?? 0,
    b.reviewed_at_unix_ms ?? 0,
    b.executed_at_unix_ms ?? 0,
    b.finalized_at_unix_ms ?? 0,
    b.escrow_posted,
    !!b.restore_posted,
    !!b.paid_posted,
  ].join("|");
}

const DECISIONS: Record<string, { label: string; tone: Tone }> = {
  APPROVE: { label: "Approved", tone: "success" },
  REJECT: { label: "Rejected", tone: "muted" },
};

/** One reviewer decision as the approvals list shows it. */
export function decisionLabel(decision: string): string {
  return DECISIONS[decision]?.label ?? decision;
}

export function decisionTone(decision: string): Tone {
  return DECISIONS[decision]?.tone ?? "muted";
}

/** How many APPROVE decisions the batch already holds. */
export function approvalCount(b: PayoutBatchItem): number {
  return (b.approvals ?? []).filter((a) => a.decision === "APPROVE").length;
}

export interface BatchAbilities {
  approve: boolean;
  reject: boolean;
  cancel: boolean;
  execute: boolean;
  complete: boolean;
  fail: boolean;
  /**
   * This admin prepared the batch: they may approve it (SM-914) and
   * cancel it, never reject it.
   */
  ownRequest: boolean;
  /** This admin's approval is already on the batch. */
  alreadyApproved: boolean;
  /** Approvals still needed before the batch is APPROVED. */
  approvalsRemaining: number;
}

/**
 * What an admin holding (or not holding) BROKER_PAYOUT_REVIEW may do to
 * b. One admin may see a batch through alone (PO, SM-914): the admin who
 * prepared it may approve it, and one approval is enough — broker-service
 * creates every new batch with approvals_required = 1. Reject is still
 * never the requester's (SELF_REJECTION: they cancel instead), and it is
 * one decision per admin — broker-service keeps one row per reviewer and
 * refuses a second decision (approve OR reject) from an admin already on
 * the batch with 409 BROKER_APPROVAL_INVALID (ALREADY_DECIDED). So an
 * old batch still asking for two approvals needs a second, DISTINCT
 * approver, and the first cannot then reject. Cancel is open to any
 * reviewer while nothing has been paid — the way out for a requester, or
 * an approver who changes their mind; execute, complete and fail follow
 * the state machine.
 */
export function batchAbilities(b: PayoutBatchItem, myAdminID: string, canReview: boolean): BatchAbilities {
  const me = myAdminID.trim();
  // An unloaded admin id is nobody — never "everybody's own".
  const ownRequest = Boolean(me) && b.requested_by === me;
  const alreadyApproved = Boolean(me) && (b.approvals ?? []).some((a) => a.approver_id === me);
  const reviewing = canReview && b.state === "PENDING_REVIEW";
  return {
    approve: reviewing && !alreadyApproved,
    reject: reviewing && !ownRequest && !alreadyApproved,
    cancel: canReview && (b.state === "PENDING_REVIEW" || b.state === "APPROVED"),
    execute: canReview && b.state === "APPROVED",
    complete: canReview && b.state === "PROCESSING",
    fail: canReview && b.state === "PROCESSING",
    ownRequest,
    alreadyApproved,
    approvalsRemaining: Math.max(0, (b.approvals_required || 1) - approvalCount(b)),
  };
}

// ── the batch's supporting earnings (SM-710 AC 4) ────────────────────
//
// The panel lists every earning the batch holds or held, a page at a
// time (broker_payout_batch_items, by earning id). An earning's state is
// its CURRENT one: after a batch fails, is rejected or is cancelled, its
// earnings read AVAILABLE again (or reserved by a later batch).

const EARNING_ORIGINS: Record<string, { label: string }> = {
  EARNED: { label: "Earned" },
  ADJUSTMENT: { label: "Adjustment" },
  REASSIGNMENT: { label: "Reassignment" },
};

export function earningOriginLabel(origin: string): string {
  return EARNING_ORIGINS[origin]?.label ?? origin;
}

/**
 * The record an ADJUSTMENT or REASSIGNMENT earning came from — its
 * adjustment or reassignment id — or undefined for an earned one (whose
 * source is the fill or redemption).
 */
export function earningOriginRef(
  e: Pick<PayoutBatchEarning, "origin" | "adjustment_id" | "reassignment_id">,
): string | undefined {
  if (e.origin === "ADJUSTMENT") return e.adjustment_id || undefined;
  if (e.origin === "REASSIGNMENT") return e.reassignment_id || undefined;
  return undefined;
}

const EARNING_SOURCES: Record<string, { label: string }> = {
  FILL: { label: "Fill" },
  REDEMPTION: { label: "Redemption" },
};

export function earningSourceLabel(kind: string): string {
  return EARNING_SOURCES[kind]?.label ?? kind;
}

const EARNING_STATES: Record<string, { label: string; tone: Tone }> = {
  PENDING: { label: "Pending", tone: "muted" },
  AVAILABLE: { label: "Available", tone: "accent" },
  PAYOUT_RESERVED: { label: "Reserved for payout", tone: "warning" },
  PAID: { label: "Paid", tone: "success" },
  VOID: { label: "Void", tone: "muted" },
  REASSIGNED: { label: "Reassigned", tone: "muted" },
};

export function earningStateLabel(state: string): string {
  return EARNING_STATES[state]?.label ?? state;
}

export function earningStateTone(state: string): Tone {
  return EARNING_STATES[state]?.tone ?? "muted";
}

/**
 * Whether the earning's fee role adds anything beside its source: a
 * fill's MAKER / TAKER does; a redemption's role is always REDEMPTION,
 * which the source already says.
 */
export function showsEarningRole(e: Pick<PayoutBatchEarning, "role" | "source_kind">): boolean {
  return Boolean(e.role) && e.source_kind !== "REDEMPTION";
}

export interface EarningsCheck {
  /** The canonical sum of this page's amounts (signed: an adjustment may debit). */
  pageSum: string;
  /** Every earning of the batch is on this page. */
  wholeBatch: boolean;
  /** broker-service counts a different number of earnings than the batch's item_count. */
  countMismatch: boolean;
  /** The whole batch is on this page and its amounts do not sum to the batch total. */
  sumMismatch: boolean;
}

/**
 * Whether the page of earnings the panel shows supports the batch it
 * belongs to: as many earnings as item_count, and — once every earning
 * is on one page — amounts that add up to the total. Either mismatch is
 * a reason not to approve.
 */
export function earningsCheck(
  b: Pick<PayoutBatchItem, "item_count" | "total">,
  page: PageInfo,
  items: Pick<PayoutBatchEarning, "amount">[],
): EarningsCheck {
  let sum = 0n;
  for (const e of items) {
    const v = (e.amount ?? "").trim();
    if (/^-?\d+$/.test(v)) sum += BigInt(v);
  }
  const countMismatch = page.total !== b.item_count;
  const wholeBatch = !countMismatch && page.offset === 0 && items.length === page.total;
  const total = (b.total ?? "").trim();
  const sumMismatch = wholeBatch && (!/^-?\d+$/.test(total) || BigInt(total) !== sum);
  return { pageSum: sum.toString(), wholeBatch, countMismatch, sumMismatch };
}

/**
 * A refusal as the operator should see it: broker-service's code first,
 * verbatim (BROKER_APPROVAL_INVALID, BROKER_PAYOUT_BATCH_TRANSITION_INVALID,
 * …), then its message — the code is what finance quotes in a ticket.
 */
export function refusalText(e: unknown): string | null {
  if (!e) return null;
  if (e instanceof APIError) {
    if (e.code && e.code !== "UNKNOWN") return e.message ? `${e.code}: ${e.message}` : e.code;
    return e.message || `HTTP ${e.status}`;
  }
  return (e as Error)?.message || "The action could not be applied.";
}
